/**
 * «Данные договора» (право `contract_edit`, **пишет в боевую CRM**): инлайн-форма полей договора —
 * ФИО, телефон, почта, мессенджер, даты проката, оплачено дней, салон, категория, доставка и забор
 * с адресами, паспорт. Уходят только изменённые поля (`patchContract`); пустая дата — `""`.
 * Ошибка поля подсвечивается по `ApiFailure.items[].field`. Ctrl/⌘+Enter — сохранить, Esc — закрыть.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { Combo } from "@/components/Combo";
import { Field, WriteOutcome, inputCls } from "@/components/contract/ui";
import { DatePick } from "@/components/DateTimePick";
import { ApiFailure } from "@/lib/api";
import {
  type RawContract,
  DAYS_MAX,
  contractOptionsQuery,
  isStale,
  patchContract,
  phoneKey,
  useContractRefresh,
  writeError,
} from "@/lib/contract";
import { btn } from "@/lib/panelBtn";

const ok = "border-border";
const bad = "border-err";

type D = RawContract["dogovor"];
const str = (d: D, k: string) => (d[k] == null ? "" : String(d[k]));
const day = (d: D, k: string) => str(d, k).slice(0, 10);
const flag = (d: D, k: string): boolean | null => (d[k] == null || d[k] === "" ? null : Number(d[k]) === 1);

type Form = {
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  dogovorMessengerStatusID: string;
  rentStart: string;
  rentEnd: string;
  daysPayed: string;
  locationID: string;
  dogovorCategoryID: string;
  deliveryStart: boolean | null;
  deliveryStartAdress: string;
  deliveryEnd: boolean | null;
  deliveryEndAdress: string;
  pass: string;
};

const initial = (d: D): Form => ({
  customerName: str(d, "customerName"),
  customerPhone: str(d, "customerPhone"),
  customerEmail: str(d, "customerEmail"),
  dogovorMessengerStatusID: str(d, "dogovorMessengerStatusID"),
  rentStart: day(d, "rentStart"),
  rentEnd: day(d, "rentEnd"),
  daysPayed: str(d, "daysPayed"),
  locationID: str(d, "locationID"),
  dogovorCategoryID: str(d, "dogovorCategoryID"),
  deliveryStart: flag(d, "deliveryStart"),
  deliveryStartAdress: str(d, "deliveryStartAdress"),
  deliveryEnd: flag(d, "deliveryEnd"),
  deliveryEndAdress: str(d, "deliveryEndAdress"),
  pass: str(d, "pass"),
});

/** Изменённые поля в типах бэкенда и найденные клиентом ошибки по полям. */
function diff(was: Form, now: Form): { changed: Record<string, unknown>; problems: Record<string, string> } {
  const changed: Record<string, unknown> = {};
  const problems: Record<string, string> = {};
  const text = (k: keyof Form, max: number, label: string) => {
    const v = (now[k] as string).trim();
    if (v.length > max) problems[k] = `${label}: не больше ${max} символов`;
    else if (v !== (was[k] as string).trim()) changed[k] = v;
  };
  // Правило проверяем только у изменённого поля: бэкенд тоже проверяет лишь присланное,
  // а старый договор с коротким телефоном или пустой категорией не должен мешать править остальное.
  const required = (k: keyof Form, label: string) => {
    if (now[k] !== was[k] && !(now[k] as string).trim()) problems[k] = `${label}: обязательное поле`;
  };
  text("customerName", 128, "ФИО");
  required("customerName", "ФИО");
  text("customerEmail", 64, "Почта");
  text("pass", 32, "Паспорт");
  text("deliveryStartAdress", 1024, "Адрес доставки");
  text("deliveryEndAdress", 1024, "Адрес забора");
  if (now.customerPhone.trim() !== was.customerPhone.trim()) {
    const phone = phoneKey(now.customerPhone);
    if (phone.length < 11 || phone.length > 15) problems.customerPhone = "Телефон — 11 цифр";
    else if (phone !== phoneKey(was.customerPhone)) changed.customerPhone = now.customerPhone.trim();
  }
  for (const k of ["rentStart", "rentEnd"] as const) if (now[k] !== was[k]) changed[k] = now[k];
  const days = now.daysPayed.trim();
  if (days !== was.daysPayed.trim()) {
    if (!/^\d+$/.test(days) || Number(days) > DAYS_MAX) problems.daysPayed = `Оплачено дней — целое от 0 до ${DAYS_MAX}`;
    else changed.daysPayed = Number(days);
  }
  for (const [k, label] of [
    ["dogovorMessengerStatusID", "Мессенджер"],
    ["locationID", "Салон"],
    ["dogovorCategoryID", "Категория"],
  ] as const) {
    required(k, label);
    if (now[k] && now[k] !== was[k]) changed[k] = Number(now[k]);
  }
  for (const k of ["deliveryStart", "deliveryEnd"] as const) if (now[k] !== was[k] && now[k] !== null) changed[k] = Number(now[k]);
  return { changed, problems };
}

export function FieldsForm({
  dealId,
  dogovorId,
  chatId,
  contract,
  onClose,
}: {
  dealId: number;
  dogovorId: number;
  chatId: number | null;
  contract: RawContract;
  onClose: () => void;
}) {
  const refresh = useContractRefresh(dealId, dogovorId, chatId);
  const options = useQuery(contractOptionsQuery());
  // Снимок на открытии формы: после 409 `contract` перечитан со свежей версией, а «изменено» считаем
  // от снимка — уходит только то, что правил сотрудник, чужая правка нетронутых полей не откатится.
  const [was] = useState(() => initial(contract.dogovor));
  const [f, setF] = useState<Form>(was);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  const { changed, problems } = diff(was, f);
  const empty = Object.keys(changed).length === 0;
  const hasProblems = Object.keys(problems).length > 0;

  const save = useMutation({
    mutationFn: () => patchContract(dogovorId, changed, contract.version, chatId),
    onSettled: refresh,
  });
  const done = save.data;
  // Ошибки бэкенда по полям (BAD_FIELD / BAD_PHONE…) — подсветка рядом с полем.
  const server: Record<string, string> = {};
  if (save.error instanceof ApiFailure) for (const i of save.error.items) if (i.field) server[i.field] = i.message;
  const known = Object.keys(was);
  const stray = save.error instanceof ApiFailure ? save.error.items.filter((i) => !i.field || !known.includes(i.field)) : [];
  const err = (k: keyof Form) => problems[k] ?? server[k];
  const cls = (k: keyof Form) => `${inputCls} ${err(k) ? bad : ok}`;
  const ready = !empty && !hasProblems && !save.isPending && !done;

  const opt = (list: { id: number; name: string }[] | undefined, current: string) => {
    const o = (list ?? []).map((x) => ({ value: String(x.id), label: x.name }));
    // Значение договора, которого нет в справочнике, не теряем.
    return current && !o.some((x) => x.value === current) ? [...o, { value: current, label: `№ ${current}` }] : o;
  };

  const flagRow = (k: "deliveryStart" | "deliveryEnd", ak: "deliveryStartAdress" | "deliveryEndAdress", label: string) => (
    <div className="flex w-full flex-col gap-1">
      <label className="flex items-center gap-1.5">
        <input type="checkbox" checked={f[k] === true} onChange={(e) => set(k, e.target.checked)} />
        {label}
      </label>
      {err(k) && <span className="text-[11.5px] text-err">{err(k)}</span>}
      {f[k] === true && (
        <Field label="Адрес" error={err(ak)} wide>
          <input className={cls(ak)} value={f[ak]} onChange={(e) => set(ak, e.target.value)} />
        </Field>
      )}
    </div>
  );

  return (
    <form
      className="flex flex-col gap-2 rounded-[var(--radius-ui)] border border-border bg-bg p-2 text-[12.5px]"
      onSubmit={(e) => (e.preventDefault(), ready && save.mutate())}
      onKeyDown={(e) => {
        // Esc внутри открытого списка/календаря закрывает только его, а не всю форму.
        const popup = e.currentTarget.querySelector('[data-popup],[role="dialog"]');
        if (e.key === "Escape" && !save.isPending && !popup) onClose();
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) (e.preventDefault(), ready && save.mutate());
      }}
      data-annot={`данные договора ${dogovorId}`}
    >
      <fieldset disabled={save.isPending || Boolean(done)} className="flex flex-wrap gap-2">
        <Field label="ФИО" error={err("customerName")}>
          <input className={cls("customerName")} value={f.customerName} onChange={(e) => set("customerName", e.target.value)} autoFocus />
        </Field>
        <Field label="Телефон" error={err("customerPhone")}>
          <input className={cls("customerPhone")} inputMode="tel" value={f.customerPhone} onChange={(e) => set("customerPhone", e.target.value)} />
        </Field>
        <Field label="Почта" error={err("customerEmail")}>
          <input className={cls("customerEmail")} inputMode="email" value={f.customerEmail} onChange={(e) => set("customerEmail", e.target.value)} />
        </Field>
        <Field label="Мессенджер" error={err("dogovorMessengerStatusID")}>
          <Combo
            label="Мессенджер"
            value={f.dogovorMessengerStatusID}
            options={opt(options.data?.messengers, f.dogovorMessengerStatusID)}
            onChange={(v) => set("dogovorMessengerStatusID", v)}
          />
        </Field>
        <Field label="Начало проката" error={err("rentStart")}>
          <DatePick label="Начало проката" value={f.rentStart} onChange={(v) => set("rentStart", v)} placeholder="не задано" />
        </Field>
        <Field label="Конец проката" error={err("rentEnd")}>
          <DatePick label="Конец проката" value={f.rentEnd} onChange={(v) => set("rentEnd", v)} placeholder="не задан" />
        </Field>
        <Field label="Оплачено дней" error={err("daysPayed")}>
          <input className={cls("daysPayed")} inputMode="numeric" value={f.daysPayed} onChange={(e) => set("daysPayed", e.target.value)} />
        </Field>
        <Field label="Салон" error={err("locationID")}>
          <Combo label="Салон" value={f.locationID} options={opt(options.data?.locations, f.locationID)} onChange={(v) => set("locationID", v)} />
        </Field>
        <Field label="Категория" error={err("dogovorCategoryID")}>
          <Combo
            label="Категория"
            value={f.dogovorCategoryID}
            options={opt(options.data?.categories, f.dogovorCategoryID)}
            onChange={(v) => set("dogovorCategoryID", v)}
          />
        </Field>
        <Field label="Серия и номер паспорта" error={err("pass")}>
          <input className={cls("pass")} value={f.pass} onChange={(e) => set("pass", e.target.value)} />
        </Field>
        {flagRow("deliveryStart", "deliveryStartAdress", "Доставка")}
        {flagRow("deliveryEnd", "deliveryEndAdress", "Забор")}
      </fieldset>
      {options.isError && <div className="text-err">Справочники не загрузились: {options.error.message}</div>}
      {save.isError && (stray.length > 0 || !(save.error instanceof ApiFailure)) && (
        <div className="whitespace-pre-wrap text-err">
          {isStale(save.error) || !(save.error instanceof ApiFailure) ? writeError(save.error) : stray.map((i) => i.message).join(" · ")}
        </div>
      )}
      {done && <WriteOutcome r={done} ok="Данные записаны в CRM" />}
      <div className="flex flex-wrap items-center gap-2">
        {!done && (
          <button type="submit" className={btn.primary} disabled={!ready}>
            {save.isPending ? "Записываю в CRM…" : "Сохранить"}
          </button>
        )}
        <button type="button" className={btn.quiet} onClick={onClose} disabled={save.isPending}>
          {done ? "Закрыть" : "Отмена"}
        </button>
        {!done && empty && !hasProblems && <span className="text-[11.5px] text-fg-muted">Изменений нет</span>}
        {!done && <span className="text-[11.5px] text-fg-faint">Ctrl/⌘+Enter — сохранить, Esc — закрыть</span>}
      </div>
    </form>
  );
}
